/**
 * Distribution Application Service
 *
 * This service orchestrates use cases and handles application-level concerns.
 * It follows the Application Service pattern for clean architecture.
 *
 * @module DistributionApplicationService
 */

import {IDistributionApplicationService} from '../interfaces/IDistributionApplicationService';
import {
  DistributionRecord,
  DistributionMarketer,
  DistributionProduct,
  AvailableSerial,
  CreateDeliveryForm,
  UpdateDeliveryForm,
  DistributionFilters
} from '../../models/distribution.model';
import {Pagination} from '../../../../Data/types/BaseResponse';

// Use Case Dependencies
import {GetDistributionHistoryUseCase} from '../../useCases/GetDistributionHistoryUseCase';
import {CreateDeliveryUseCase} from '../../useCases/CreateDeliveryUseCase';
import {UpdateDeliveryUseCase} from '../../useCases/UpdateDeliveryUseCase';
import {DeleteTransactionUseCase} from '../../useCases/DeleteTransactionUseCase';
import {GetMarketersForDistributionUseCase} from '../../useCases/GetMarketersForDistributionUseCase';
import {GetProductsForDistributionUseCase} from '../../useCases/GetProductsForDistributionUseCase';
import {GetAvailableSerialsUseCase} from '../../useCases/GetAvailableSerialsUseCase';
import {GetTrackedSerialsUseCase} from '../../useCases/GetTrackedSerialsUseCase';

export class DistributionApplicationService implements IDistributionApplicationService {
  constructor (
    private getDistributionHistoryUseCase: GetDistributionHistoryUseCase,
    private createDeliveryUseCase: CreateDeliveryUseCase,
    private updateDeliveryUseCase: UpdateDeliveryUseCase,
    private deleteTransactionUseCase: DeleteTransactionUseCase,
    private getMarketersForDistributionUseCase: GetMarketersForDistributionUseCase,
    private getProductsForDistributionUseCase: GetProductsForDistributionUseCase,
    private getAvailableSerialsUseCase: GetAvailableSerialsUseCase,
    private getTrackedSerialsUseCase: GetTrackedSerialsUseCase
  ) {}

  async getDistributionHistory (filters?: DistributionFilters, page?: number, limit?: number): Promise<{ items: DistributionRecord[]; pagination?: Pagination }> {
    return this.getDistributionHistoryUseCase.execute(filters, page, limit);
  }

  async createDelivery (deliveryData: CreateDeliveryForm): Promise<{ record: DistributionRecord; message?: string }> {
    return this.createDeliveryUseCase.execute(deliveryData);
  }

  async updateDelivery (id: string, updateData: UpdateDeliveryForm): Promise<DistributionRecord> {
    return this.updateDeliveryUseCase.execute(id, updateData);
  }

  async deleteTransaction (transactionId: string): Promise<void> {
    return this.deleteTransactionUseCase.execute(transactionId);
  }

  async getDeliveryById (id: string): Promise<DistributionRecord> {
    // This would be implemented when the use case is created
    throw new Error('Method not implemented');
  }

  async getMarketersForDistribution (search?: string): Promise<DistributionMarketer[]> {
    return this.getMarketersForDistributionUseCase.execute(search);
  }

  async getProductsForDistribution (search?: string): Promise<DistributionProduct[]> {
    return this.getProductsForDistributionUseCase.execute(search);
  }

  async getAvailableSerials (productId: string): Promise<AvailableSerial[]> {
    return this.getAvailableSerialsUseCase.execute(productId);
  }

  async getTrackedSerials (filters?: { productId?: string; locationId?: string; status?: string }): Promise<AvailableSerial[]> {
    return this.getTrackedSerialsUseCase.execute(filters);
  }
}
